import { useCallback, useEffect, useState } from "react";
import { Box, Typography, Grid, Button, TextField } from "@mui/material";
import { getWellConfig } from "../../utils/wellsConfig";
import { PRC_WELL } from "../../utils/PRC_WELL";
import axios from "../../utils/axiosInstance";
import { ProgressDialog } from "./progressPopup";
import { toast } from "react-toastify";
import { getUIDescription } from "../SiteAdmin/customerGroupDetails/AssigningCustomerGroupDevice";

export default function AnalogSetpointsTab({
  analogSetpointsSettings,
  optimizationSettings,
  setLoading,
  selectedTab,
  selectedDevice,
  refreshData,
}) {
  const [analogSetpointsValues, setAnalogSetpointsValues] = useState(
    analogSetpointsSettings
  );
  const [progressOpen, setProgressOpen] = useState(false);
  const [progressSuccess, setProgressSuccess] = useState(false);
  const [progressError, setProgressError] = useState("");

  const isOptimizationEnabled = useCallback(
    (toBeEnabled) => {
      const enabled = optimizationSettings.find(
        (item) => item.label === toBeEnabled
      );
      return (
        enabled?.value === "Enabled" ||
        enabled?.value === "1" ||
        enabled?.value === 1
      );
    },
    [optimizationSettings]
  );

  useEffect(() => {
    setAnalogSetpointsValues(analogSetpointsSettings);
  }, [analogSetpointsSettings]);

  const handleChange = (cardIndex, fieldIndex, newValue, subFieldKey) => {
    setAnalogSetpointsValues((prev) =>
      prev.map((card, i) =>
        i === cardIndex
          ? {
              ...card,
              fields: card.fields?.map((f, j) =>
                j === fieldIndex
                  ? f.fields
                    ? {
                        ...f,
                        fields: { ...f.fields, [subFieldKey]: newValue },
                      }
                    : { ...f, value: newValue }
                  : f
              ),
            }
          : card
      )
    );
  };

  const handleSubmit = async () => {
    // Step 1: Check if all fields are filled
    const isAnyFieldEmpty = analogSetpointsValues.some((card) => {
      // Check if any field is empty within each card
      return card.fields.some((field) => {
        if (field.fields) {
          // For fields with 'hrs', 'min', 'sec', check each one individually
          return ["hrs", "min", "sec"].some((key) => field.fields[key] === "");
        }
        // For single field inputs, just check the 'value'
        return field.value === "";
      });
    });

    if (isAnyFieldEmpty) {
      console.error("❌ Please fill all fields.");
      toast.error("❌ Please fill all fields before submitting.");
      return;
    }
    if (!selectedDevice?.id) {
      console.error("❌ No device selected");
      return;
    }
    // Step 2: Open dialog first
    setProgressOpen(true);
    setProgressSuccess(false);
    // Step 3: Give React a chance to render before doing heavy work
    setTimeout(() => {
      runDeviceUpdate();
    }, 100); // 100ms is enough to show the dialog
  };

  const runDeviceUpdate = async () => {
    let pollInterval = null;
    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    const pollForChange = async ({
      unitId,
      registerList,
      lastTime,
      lastResponse,
      durationMs,
      intervalMs,
      onSuccess,
      onTimeout,
    }) => {
      const start = Date.now();

      pollInterval = setInterval(async () => {
        try {
          const resp = await axios.post(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
            registerList
          );

          const { time: currentTime, response: currentResponse } =
            resp.data.data || {};

          const timeChanged =
            currentTime && Number(currentTime) !== Number(lastTime);

          let valueChanged = false;
          for (const key in currentResponse) {
            if (currentResponse[key] !== lastResponse[key]) {
              valueChanged = true;
              break;
            }
          }

          console.log("⏳ Polling...", {
            currentTime,
            timeChanged,
            valueChanged,
          });

          if (timeChanged && valueChanged) {
            stopPolling();
            onSuccess({
              currentTime,
              currentResponse,
            });
            console.log("🔄 Device time changed:", lastTime, "->", currentTime);
          }

          if (Date.now() - start >= durationMs) {
            stopPolling();
            onTimeout();
          }
        } catch (err) {
          console.warn("Polling error:", err.message);
        }
      }, intervalMs);
    };
    try {
      let cmd = "A";
      const unitId = selectedDevice?.id;
      const wellConfig = await getWellConfig(selectedTab);
      let registerList = [
        wellConfig.AUTO_SP,
        wellConfig.FLOW_RATE_DEADBAND,
        wellConfig.CASING_PID_AUTO_SP,
        wellConfig.CASING_PID_DEADBAND_SP,
        wellConfig.TUBING_CASING_SP,
        wellConfig.TUBING_CASING_DEBOUNCE,
        wellConfig.MAX_SHUTIN_RESET,
        wellConfig.PLG_FALL_TIMER?.[0],
        wellConfig.PLG_FALL_TIMER?.[1],
        wellConfig.PLG_FALL_TIMER?.[2],
        wellConfig.CRITICAL_FLOW_RATE_SP,
        wellConfig.CRITICAL_FLOW_RATE_DEADBAND,
        wellConfig.CRITICAL_FLOW_RATE_DEBOUNCE,
        wellConfig.MAX_AFTERFLOW_RESET,
        wellConfig.CASING_RISE_SP,
        wellConfig.CASING_RISE_DEBOUNCE,
      ];
      console.log("register list in body", registerList);
      // Step 1: get initial time
      const initialResp = await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/device_get_by_register/${unitId}`,
        registerList
      );
      let lastTime = Number(initialResp.data.data?.time || 0);
      let lastResponse = initialResp.data.data?.response || {};
      console.log("Initial time:", lastTime);
      console.log("regList:", lastResponse);
      const flowRateAutomaticSP = PRC_WELL.getAnalogSetpointsValueFromWell(
        analogSetpointsValues,
        "Flow Rate Automatic SP"
      );
      const flowRateAutomaticSPValue =
        parseFloat(flowRateAutomaticSP?.value) * 10;

      const flowRateDeadbandSP = PRC_WELL.getAnalogSetpointsValueFromWell(
        analogSetpointsValues,
        "Flow Rate Deadband SP"
      );
      const flowRateDeadbandSPValue =
        parseFloat(flowRateDeadbandSP?.value) * 10;

      const casingPIDAutoSP = PRC_WELL.getAnalogSetpointsValueFromWell(
        analogSetpointsValues,
        "Casing PID Automatic SP"
      );
      const casingPIDAutoSPValue = parseFloat(casingPIDAutoSP?.value) * 10;

      const casingPIDDeadbandSP = PRC_WELL.getAnalogSetpointsValueFromWell(
        analogSetpointsValues,
        "Casing PID Deadband SP"
      );
      const casingPIDDeadbandSPValue =
        parseFloat(casingPIDDeadbandSP?.value) * 10;

      const tubingCasingSP = PRC_WELL.getAnalogSetpointsValueFromWell(
        analogSetpointsValues,
        "Tubing : Casing SP"
      );
      const tubingCasingSPValue = tubingCasingSP?.value;

      const tubingCasingDebounce = PRC_WELL.getAnalogSetpointsValueFromWell(
        analogSetpointsValues,
        "Tubing : Casing Debounce"
      );
      const tubingCasingDebounceValue = tubingCasingDebounce?.value;

      const maxShutinReset = PRC_WELL.getAnalogSetpointsValueFromWell(
        analogSetpointsValues,
        "Maximum Shutin Resets"
      );
      const maxShutinResetValue = maxShutinReset?.value;

      const plungerFallTimer = PRC_WELL.getAnalogSetpointsValueFromWell(
        analogSetpointsValues,
        "Plunger Fall Timer"
      );
      const plungerFallTimerHour = plungerFallTimer?.fields.hrs;
      const plungerFallTimerMin = plungerFallTimer?.fields.min;
      const plungerFallTimerSec = plungerFallTimer?.fields.sec;

      const criticalFlowrateSP = PRC_WELL.getAnalogSetpointsValueFromWell(
        analogSetpointsValues,
        "Critical Flow Rate SP"
      );
      const criticalFlowrateSPValue =
        parseFloat(criticalFlowrateSP?.value) * 10;

      const criticalFlowrateDeadband = PRC_WELL.getAnalogSetpointsValueFromWell(
        analogSetpointsValues,
        "Critical Flow Rate Deadband"
      );
      const criticalFlowrateDeadbandValue =
        parseFloat(criticalFlowrateDeadband?.value) * 10;

      const criticalFlowrateDebounce = PRC_WELL.getAnalogSetpointsValueFromWell(
        analogSetpointsValues,
        "Critical Flow Rate Debounce"
      );
      const criticalFlowrateDebounceValue = criticalFlowrateDebounce?.value;

      const maxAFReset = PRC_WELL.getAnalogSetpointsValueFromWell(
        analogSetpointsValues,
        "Maximum Afterflow Resets"
      );
      const maxAFResetValue = maxAFReset?.value;

      const casingRiseSP = PRC_WELL.getAnalogSetpointsValueFromWell(
        analogSetpointsValues,
        "Casing Rise SP"
      );
      const casingRiseSPValue = parseFloat(casingRiseSP?.value) * 10;

      const casingRiseDebounce = PRC_WELL.getAnalogSetpointsValueFromWell(
        analogSetpointsValues,
        "Casing Rise Debounce"
      );
      const casingRiseDebounceValue = casingRiseDebounce?.value;

      cmd +=
        "," + (wellConfig.AUTO_SP - 40001) + "," + flowRateAutomaticSPValue;
      cmd +=
        "," +
        (wellConfig.FLOW_RATE_DEADBAND - 40001) +
        "," +
        flowRateDeadbandSPValue;
      cmd +=
        "," +
        (wellConfig.CASING_PID_AUTO_SP - 40001) +
        "," +
        casingPIDAutoSPValue;
      cmd +=
        "," +
        (wellConfig.CASING_PID_DEADBAND_SP - 40001) +
        "," +
        casingPIDDeadbandSPValue;
      cmd +=
        "," + (wellConfig.TUBING_CASING_SP - 40001) + "," + tubingCasingSPValue;
      cmd +=
        "," +
        (wellConfig.TUBING_CASING_DEBOUNCE - 40001) +
        "," +
        tubingCasingDebounceValue;
      cmd +=
        "," + (wellConfig.MAX_SHUTIN_RESET - 40001) + "," + maxShutinResetValue;
      cmd +=
        "," +
        (wellConfig.PLG_FALL_TIMER?.[0] - 40001) +
        "," +
        plungerFallTimerHour;
      cmd +=
        "," +
        (wellConfig.PLG_FALL_TIMER?.[1] - 40001) +
        "," +
        plungerFallTimerMin;
      cmd +=
        "," +
        (wellConfig.PLG_FALL_TIMER?.[2] - 40001) +
        "," +
        plungerFallTimerSec;
      cmd +=
        "," +
        (wellConfig.CRITICAL_FLOW_RATE_SP - 40001) +
        "," +
        criticalFlowrateSPValue;
      cmd +=
        "," +
        (wellConfig.CRITICAL_FLOW_RATE_DEADBAND - 40001) +
        "," +
        criticalFlowrateDeadbandValue;
      cmd +=
        "," +
        (wellConfig.CRITICAL_FLOW_RATE_DEBOUNCE - 40001) +
        "," +
        criticalFlowrateDebounceValue;
      cmd +=
        "," + (wellConfig.MAX_AFTERFLOW_RESET - 40001) + "," + maxAFResetValue;
      cmd +=
        "," + (wellConfig.CASING_RISE_SP - 40001) + "," + casingRiseSPValue;
      cmd +=
        "," +
        (wellConfig.CASING_RISE_DEBOUNCE - 40001) +
        "," +
        casingRiseDebounceValue;

      // Step 2: Send A command
      await axios.post(
        `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
        { unitId, cmd,UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${cmd}`,},
        {
          headers: {
            id: "anything",
            "Content-Type": "application/json",
          },
        }
      );
      console.log("Sending command:", cmd);
      console.log("🟡 Polling for 30s...");
      // STEP 3: poll for 30s BEFORE sending P
      pollForChange({
        unitId,
        registerList,
        lastTime,
        lastResponse,
        durationMs: 30000,
        intervalMs: 4000,

        onSuccess: () => {
          console.log("✅ Update done after A command");
          setProgressSuccess(true);
        },

        onTimeout: async () => {
          console.log("⏰ No change after A, sending P command...");

          // === STEP 4: build & send P ===
          let pCmd = cmd.replace("A", "P");
          pCmd = pCmd.replace(/(-?\d+),\s*(?:-?\d+(?:\.\d+)?|NaN)/gi, "$1,1");
          pCmd += `,${wellConfig.STATE - 40001},1`;

          await axios.post(
            `${process.env.REACT_APP_API_GATEAWAY}/iot/control/precision/plg`,
            { unitId, cmd: pCmd,UI_Description: `${getUIDescription()}\n Send Command to the controller \nCmd: ${pCmd}`,},
            { headers: { id: "anything", "Content-Type": "application/json" } }
          );

          console.log("🟢 P command sent, polling again...");

          // === STEP 5: poll AFTER P ===
          pollForChange({
            unitId,
            registerList,
            lastTime,
            lastResponse,
            durationMs: 170000,
            intervalMs: 4000,

            onSuccess: () => {
              console.log("✅ Update done after P command");
              setProgressSuccess(true);
            },

            onTimeout: () => {
              console.warn("❌ Timeout after P command");
              setProgressError(
                "⏰ Timeout waiting for device response. Please try again."
              );
            },
          });
        },
      });
    } catch (err) {
      console.error("plgSendCommand failed:", err);
      setProgressOpen(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid
      sx={{
        backgroundColor: "#374151",
        color: "#fff",
        p: 2,
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Grid sx={{ display: "flex", justifyContent: "end" }}>
        <Button
          color="primary"
          variant="contained"
          sx={{ fontSize: 12 }}
          size="small"
          onClick={() => handleSubmit()}
        >
          Submit
        </Button>
      </Grid>
      {/* Render two cards per row */}
      {analogSetpointsValues
        .reduce((rows, card, index) => {
          if (index % 2 === 0)
            rows.push(analogSetpointsValues.slice(index, index + 2));
          return rows;
        }, [])
        .map((row, rowIndex) => (
          <Grid container spacing={4} key={rowIndex}>
            {row.map((card, cardIndex) => (
              <Grid
                size={{ xs: 12, lg: card.fullWidth ? 12 : 6 }}
                key={card.title}
              >
                <Box sx={{ border: "1px solid #fff" }}>
                  {/* Card title */}
                  <Box sx={{ bgcolor: "#1e293b", px: 1, py: 1 }}>
                    <Typography
                      sx={{
                        color: "#fff",
                        fontWeight: "bold",
                        fontSize: 14,
                      }}
                    >
                      {card.title}
                    </Typography>
                  </Box>

                  {/* Enabled / Disabled logic */}
                  {!card.toBeEnabled ||
                  isOptimizationEnabled(card?.toBeEnabled) ? (
                    card.fields.map((field, fieldIndex) => (
                      <Box
                        key={fieldIndex}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          borderBottom: "1px solid #827f7fff",
                          py: 1.2,
                          px: 1,
                        }}
                      >
                        {/* Field label */}
                        <Typography
                          sx={{
                            flex: 2,
                            color: "#ffffffff",
                            fontWeight: 600,
                            fontSize: 13,
                          }}
                        >
                          {field.label}
                        </Typography>

                        {/* Multi-field input (hrs/min/sec) */}
                        {field.fields ? (
                          <Grid
                            container
                            spacing={1}
                            alignItems="center"
                            sx={{ flex: 3, justifyContent: "flex-end" }}
                          >
                            {["hrs", "min", "sec"].map(
                              (key) =>
                                field.fields[key] !== undefined && (
                                  <Grid key={key} sx={{ width: "22%" }}>
                                    <TextField
                                      label={key.toUpperCase()}
                                      fullWidth
                                      size="small"
                                      value={field.fields[key]}
                                      onChange={(e) =>
                                        handleChange(
                                          cardIndex + rowIndex * 2,
                                          fieldIndex,
                                          e.target.value,
                                          key
                                        )
                                      }
                                      InputLabelProps={{
                                        style: { color: "#fff", fontSize: 14 },
                                      }}
                                      InputProps={{
                                        sx: {
                                          color: "#fff",
                                          backgroundColor: "#1f2937",
                                          padding: 0.2,
                                          fontSize: 12,
                                        },
                                      }}
                                    />
                                  </Grid>
                                )
                            )}
                          </Grid>
                        ) : (
                          // Single input
                          <Grid
                            container
                            spacing={1}
                            alignItems="center"
                            sx={{ flex: 3, justifyContent: "flex-end" }}
                          >
                            <Grid>
                              <TextField
                                label={field?.unit}
                                fullWidth
                                size="small"
                                value={field.value}
                                onChange={(e) =>
                                  handleChange(
                                    cardIndex + rowIndex * 2,
                                    fieldIndex,
                                    e.target.value
                                  )
                                }
                                InputLabelProps={{
                                  style: { color: "#fff", fontSize: 14 },
                                }}
                                InputProps={{
                                  sx: {
                                    color: "#fff",
                                    backgroundColor: "#1f2937",
                                    padding: 0.2,
                                    fontSize: 12,
                                  },
                                }}
                              />
                            </Grid>
                          </Grid>
                        )}
                      </Box>
                    ))
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        py: 4,
                      }}
                    >
                      <Typography
                        sx={{
                          color: "#b9babbff",
                          fontWeight: 500,
                          fontSize: 14,
                          textAlign: "center",
                        }}
                      >
                        {card.disabledText}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        ))}
      <ProgressDialog
        open={progressOpen}
        isSuccess={progressSuccess}
        errorMessage={progressError}
        onClose={() => {
          setProgressOpen(false);
          setProgressError("");
        }}
        onRefresh={async () => {
          setProgressOpen(false);
          setProgressError("");
          await refreshData();
        }}
        loadingMessage="Check Device Response. Operation in-progress ..."
        successMessage="Successfully updated!"
      />
    </Grid>
  );
}
